import {
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import * as React from 'react';
import AsyncSelect from 'react-select/async';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import rest from '../../../common/services/restFootData';
import { getFDProjectUrl } from '../../../common/services/project';
import { IBookmaker, IBookmakerOdds } from '../typings';

interface IOption {
  value: string;
  label: string;
}

interface IProps {
  bookmaker: IBookmaker;
  // Bound thunk from the connector; resolves to { collection, totalItems }.
  fetchBookmakerOdds: any;
  intl: any;
}

interface IState {
  competition: IOption | null;
  tournament: IOption | null;
  season: IOption | null;
  team: IOption | null;
  page: number;
  itemsPerPage: number;
  odds: IBookmakerOdds[];
  totalItems: number;
  loading: boolean;
}

// Direction of the odds move since the previous import (a shorter odd = more
// likely). Purely indicative.
const trend = (current?: number, previous?: number): string => {
  if (current == null || previous == null || current === previous) {
    return '=';
  }
  return current < previous ? '↓' : '↑';
};

class BookmakerOddsComponent extends React.Component<IProps, IState> {
  public state: IState = {
    competition: null,
    tournament: null,
    season: null,
    team: null,
    page: 0,
    itemsPerPage: 20,
    odds: [],
    totalItems: 0,
    loading: false,
  };

  public componentDidMount() {
    this.load();
  }

  private load = () => {
    const { bookmaker, fetchBookmakerOdds } = this.props;
    const { competition, tournament, season, team, page, itemsPerPage } = this.state;

    this.setState({ loading: true });
    fetchBookmakerOdds(bookmaker.id, {
      competition: competition ? competition.value : undefined,
      tournament: tournament ? tournament.value : undefined,
      season: season ? season.value : undefined,
      team: team ? team.value : undefined,
      page: page + 1,
      itemsPerPage,
    }).then(({ collection, totalItems }) => {
      this.setState({ odds: collection, totalItems, loading: false });
    });
  };

  // react-select async loaders — each filter hits its collection endpoint.
  private loadCompetitions = (input: string) =>
    rest
      .get('competitions', { params: { name: input, groups: ['read_link'], itemsPerPage: 15 } })
      .then(res => this.toOptions(res, c => c.name));

  private loadTournaments = (input: string) => {
    const { competition } = this.state;
    return rest
      .get('tournaments', {
        params: {
          competition: competition ? competition.value : undefined,
          groups: ['read_link'],
          itemsPerPage: 30,
          name: input,
        },
      })
      .then(res => this.toOptions(res, t => t.name || t.fullName || `${t.id}`));
  };

  private loadSeasons = (input: string) =>
    rest
      .get('seasons', { params: { groups: ['read_link'], itemsPerPage: 40, 'order[name]': 'desc', name: input } })
      .then(res => this.toOptions(res, s => s.name || `${s.id}`));

  private loadTeams = (input: string) =>
    rest
      .get('teams', { params: { name: input, groups: ['read_link'], itemsPerPage: 15 } })
      .then(res => this.toOptions(res, t => t.name));

  private toOptions = (res: any, getLabel: (o: any) => string): IOption[] => {
    const collection = (res && res.data && res.data.collection) || [];
    return collection.map((o: any) => ({ value: o.id, label: getLabel(o) || `${o.id}` }));
  };

  private handleFilter = (key: 'competition' | 'tournament' | 'season' | 'team') => (option: IOption | null) => {
    const next: any = { [key]: option, page: 0 };
    // Picking another competition resets the dependent tournament.
    if (key === 'competition') {
      next.tournament = null;
    }
    this.setState(next, this.load);
  };

  private handleChangePage = (_e: any, page: number) => {
    this.setState({ page }, this.load);
  };

  private handleChangeRowsPerPage = (e: any) => {
    this.setState({ itemsPerPage: parseInt(e.target.value, 10), page: 0 }, this.load);
  };

  private renderRow = (o: IBookmakerOdds) => {
    const m = o.match || ({} as any);
    const href = `#${getFDProjectUrl()}/matches/${m.id}/odds`;

    return (
      <TableRow key={o.id}>
        <TableCell>
          <a href={href}>{m.description || m.id}</a>
        </TableCell>
        <TableCell>{m.phaseTournamentCompetitionName}</TableCell>
        <TableCell>{m.phase && m.phase.tournament ? m.phase.tournament.name : ''}</TableCell>
        <TableCell>{m.phase ? m.phase.longName : ''}</TableCell>
        <TableCell>{m.date ? moment(m.date).format('DD/MM/YYYY') : ''}</TableCell>
        <TableCell>{o.typeTranslated || o.type}</TableCell>
        <TableCell>
          {o.isLive ? (
            <Chip label={this.props.intl.messages.bookmaker_odds_live} size="small" color="secondary" />
          ) : (
            this.props.intl.messages.bookmaker_odds_prematch
          )}
        </TableCell>
        <TableCell>
          1&nbsp;{o.homeTeamWin} {trend(o.homeTeamWin, o.previousHomeTeamWin)} ·{' '}
          {this.props.intl.messages.bookmaker_odds_draw}&nbsp;{o.draw} {trend(o.draw, o.previousDraw)} · 2&nbsp;
          {o.awayTeamWin} {trend(o.awayTeamWin, o.previousAwayTeamWin)}
        </TableCell>
      </TableRow>
    );
  };

  public render() {
    const {
      intl: { messages: m },
    } = this.props;
    const { competition, tournament, season, team, odds, totalItems, itemsPerPage, page, loading } = this.state;

    const filterStyle: React.CSSProperties = {
      minWidth: 200,
      marginRight: 12,
      marginBottom: 12,
      display: 'inline-block',
    };

    return (
      <div style={{ padding: 16 }}>
        <div>
          <div style={filterStyle}>
            <AsyncSelect
              value={competition}
              isClearable
              cacheOptions
              defaultOptions
              loadOptions={this.loadCompetitions}
              onChange={this.handleFilter('competition')}
              placeholder={m.app_competition}
            />
          </div>
          <div style={filterStyle}>
            <AsyncSelect
              key={competition ? competition.value : 'no-competition'}
              value={tournament}
              isClearable
              isDisabled={!competition}
              cacheOptions
              defaultOptions
              loadOptions={this.loadTournaments}
              onChange={this.handleFilter('tournament')}
              placeholder={m.app_tournaments}
            />
          </div>
          <div style={filterStyle}>
            <AsyncSelect
              value={season}
              isClearable
              cacheOptions
              defaultOptions
              loadOptions={this.loadSeasons}
              onChange={this.handleFilter('season')}
              placeholder={m.app_seasons}
            />
          </div>
          <div style={filterStyle}>
            <AsyncSelect
              value={team}
              isClearable
              cacheOptions
              loadOptions={this.loadTeams}
              onChange={this.handleFilter('team')}
              placeholder={m.app_teams}
            />
          </div>
        </div>

        {loading ? (
          <CircularProgress size={40} style={{ margin: 24 }} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{m.app_match}</TableCell>
                <TableCell>{m.app_competition}</TableCell>
                <TableCell>{m.app_tournament}</TableCell>
                <TableCell>{m.app_phase}</TableCell>
                <TableCell>{m.field_date}</TableCell>
                <TableCell>{m.bookmaker_odds_type}</TableCell>
                <TableCell>{m.bookmaker_odds_liveColumn}</TableCell>
                <TableCell>{m.bookmaker_odds_resultLabel}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {odds.length ? (
                odds.map(this.renderRow)
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>{m.bookmaker_odds_notFound}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={this.handleChangePage}
          rowsPerPage={itemsPerPage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </div>
    );
  }
}

// @ts-ignore
export const BookmakerOdds = injectIntl(BookmakerOddsComponent) as any;
